/**
 * useApiReachability
 *
 * Detects whether the KARMA backend API is reachable from the current network.
 *
 * ## Problem this solves
 * A user may have valid auth tokens cached in the browser (from a previous
 * login on a different network) while their current network — typically a
 * corporate firewall — blocks the API domain but not the frontend domain.
 * In this situation `navigator.onLine` reports `true` and the useNetworkStatus
 * hook sees no problem, yet every API call silently fails.
 *
 * ## How it works
 * On mount the hook fires a single HEAD request to the API base URL using
 * `mode: 'no-cors'`. This intentionally opaque request never reads the
 * response body, but the browser still throws a TypeError on a true network
 * block (firewall, DNS failure, timeout). Any HTTP response — even a 403 from
 * API Gateway — means the domain is reachable and we treat it as reachable.
 *
 * ## Return value
 * | field          | type      | description                                      |
 * |----------------|-----------|--------------------------------------------------|
 * | isApiReachable | boolean   | true = API domain responded, false = blocked     |
 * | isChecking     | boolean   | true while the initial probe is in flight        |
 *
 * ## Usage
 * ```tsx
 * const { isApiReachable, isChecking } = useApiReachability()
 *
 * if (!isChecking && !isApiReachable) return <ApiBlockedBanner />
 * ```
 */

import { useEffect, useState } from 'react'

const API_URL = import.meta.env.REACT_APP_API_URL as string
const PROBE_TIMEOUT_MS = 8000

export const useApiReachability = () => {
  const [isApiReachable, setIsApiReachable] = useState(true)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    let unmounted = false
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)

    const probe = async () => {
      try {
        await fetch(API_URL, {
          method: 'HEAD',
          mode: 'no-cors',
          signal: controller.signal,
        })
        setIsApiReachable(true)
      } catch {
        // TypeError = network block by firewall/DNS.
        // AbortError from our timeout = no response within PROBE_TIMEOUT_MS.
        // AbortError from cleanup (component unmount) = don't update state.
        if (!unmounted) setIsApiReachable(false)
      } finally {
        if (!unmounted) setIsChecking(false)
        clearTimeout(timeoutId)
      }
    }

    probe()

    return () => {
      unmounted = true
      controller.abort()
      clearTimeout(timeoutId)
    }
  }, [])

  return { isApiReachable, isChecking }
}
